import { noop } from 'lodash';

import {
  DEFAULT_ENGLISH_GOOGLE_VALUE,
  DEFAULT_LANGUAGE_ENGLISH,
  GOOGLE_TRANSLATE_COOKIE_KEY,
  GOOGLE_TRANSLATE_COOKIE_OPTIONS,
  GOOGLE_TRANSLATE_COOKIE_DOMAINS,
  INITIAL_LANGUAGE,
  LANGUAGE_OPTIONS,
  SUPPORTED_HUMAN_LANGUAGES,
  I18N_TRANSLATE_COOKIE_KEY,
} from 'components/Common/GoogleTranslateV2/constants';

export const clearLanguageSpecificLocalStorage = () => {
  // Used to clear cached data that is language dependent
  const prefixes = [
    'pathUserStatementData',
    'wyrQuizData',
    'skillsAssessmentData',
  ];

  // Get all localStorage keys
  const keys = Object.keys(localStorage);

  // Remove keys that start with any of the specified prefixes
  keys.forEach(key => {
    if (prefixes.some(prefix => key.startsWith(prefix))) {
      localStorage.removeItem(key);
    }
  });
};

export const handleSetLanguage = async ({
  val,
  i18n,
  setSelectedLanguage,
  removeCookie,
  setCookie,
  selectedLanguage,
  onClose = noop,
  setUserLanguage,
}) => {
  if (selectedLanguage?.value === val?.value) {
    return;
  }

  clearLanguageSpecificLocalStorage();

  // Remove all existing googtrans cookies
  GOOGLE_TRANSLATE_COOKIE_DOMAINS.forEach(domain => {
    // Remove with standard options
    removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
      ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
      domain,
    });
    removeCookie(I18N_TRANSLATE_COOKIE_KEY, {
      ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
      domain,
    });

    // Also try without expiration
    removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
      path: '/',
      domain,
    });
    removeCookie(I18N_TRANSLATE_COOKIE_KEY, {
      path: '/',
      domain,
    });
  });

  // Also try removing without domain specification
  removeCookie(GOOGLE_TRANSLATE_COOKIE_KEY, {
    ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
  });
  removeCookie(I18N_TRANSLATE_COOKIE_KEY, {
    ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
  });

  // Reset selected language on clear
  if (!val) {
    setSelectedLanguage(INITIAL_LANGUAGE);
    await i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
    await setUserLanguage({
      payload: {
        activeLanguage: DEFAULT_LANGUAGE_ENGLISH,
      },
    });
    setCookie(GOOGLE_TRANSLATE_COOKIE_KEY, DEFAULT_ENGLISH_GOOGLE_VALUE, {
      ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
      encode: v => v,
    });
    onClose();
    window.location.reload();
    return;
  }

  // Use i18n for languages that we support
  if (val && SUPPORTED_HUMAN_LANGUAGES.includes(val.value)) {
    setSelectedLanguage(val);
    await i18n.changeLanguage(val.value);
    await setUserLanguage({
      payload: {
        activeLanguage: val.value,
      },
    });
    // Ensure dropdown persists when manually selecting English
    if (val.value === DEFAULT_LANGUAGE_ENGLISH) {
      setCookie(GOOGLE_TRANSLATE_COOKIE_KEY, DEFAULT_ENGLISH_GOOGLE_VALUE, {
        ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
        encode: v => v,
      });
    }
    onClose();
    window.location.reload();
    return;
  }

  const newLanguage = LANGUAGE_OPTIONS.find(opt => opt.value === val.value);
  setSelectedLanguage(newLanguage);
  // For unsupported languages, set default language back to English and use Google Translate
  await i18n.changeLanguage(DEFAULT_LANGUAGE_ENGLISH);
  setCookie(GOOGLE_TRANSLATE_COOKIE_KEY, `/en/${val.value}`, {
    ...GOOGLE_TRANSLATE_COOKIE_OPTIONS,
    encode: v => v,
  });
  await setUserLanguage({
    payload: {
      activeLanguage: newLanguage.value,
    },
  });
  window.location.reload();
};

export const getDropdownVisibility = ({
  isDesktop,
  isOnboarding,
  hidden,
  selectedGoogleLanguage,
  isI18NLanguageSelected,
}) => {
  if (!isDesktop) {
    return false;
  }
  if (isOnboarding) {
    return !hidden;
  }

  return !hidden || !!selectedGoogleLanguage || isI18NLanguageSelected;
};
